import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { endpoints } from '../api/endpoints';
import type { VoiceProfile, VoiceProfileInput } from '../api/types';
import { ConfirmDialog } from '../components/a11y';
import { ErrorBox, Loading, useAsync } from '../components/common';

export const EMOTIONS = ['neutral', 'cheerful', 'calm', 'sad', 'angry', 'excited', 'serious', 'friendly'] as const;
export const PROVIDERS = ['piper', 'elevenlabs', 'azure', 'google'] as const;

const EMPTY: VoiceProfileInput = {
  name: '',
  language: 'ru',
  provider: 'piper',
  voice_id: '',
  emotion: 'neutral',
  speed: 1,
  pitch: 0,
  settings: {},
};

export function VoiceProfilesPage() {
  const { t } = useTranslation();
  const profiles = useAsync(() => endpoints.voiceProfiles.list(), []);
  const voices = useAsync(() => endpoints.tts.voices().catch(() => []), []);
  const [form, setForm] = useState<VoiceProfileInput>(EMPTY);
  const [editing, setEditing] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [audio, setAudio] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<VoiceProfile | null>(null);

  const languageVoices = (voices.data ?? []).filter((v) => v.language.startsWith(form.language) && v.provider === form.provider);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      if (editing) await endpoints.voiceProfiles.update(editing, form);
      else await endpoints.voiceProfiles.create(form);
      setForm(EMPTY);
      setEditing(null);
      await profiles.reload();
    } catch (err) {
      setError(err);
    }
  };

  const act = async (fn: () => Promise<unknown>) => {
    setError(null);
    try {
      await fn();
      await profiles.reload();
    } catch (err) {
      setError(err);
    }
  };

  const preview = async (p: VoiceProfile | null) => {
    setError(null);
    try {
      const result = await endpoints.tts.generate({
        text: t('voiceProfiles.previewText'),
        voice_profile_id: p?.id ?? null,
        voice: p ? null : form.voice_id || null,
        language: p?.language ?? form.language,
        emotion: (p?.emotion ?? form.emotion) || 'neutral',
        speed: p?.speed ?? form.speed ?? 1,
        output_format: 'mp3',
        quality: 'high',
      });
      setAudio(result.audio_url);
    } catch (err) {
      setError(err);
    }
  };

  return (
    <div>
      <h1>{t('voiceProfiles.title')}</h1>
      <ErrorBox error={profiles.error || error} onRetry={profiles.reload} />
      {audio && <audio controls autoPlay src={audio} aria-label={t('voiceProfiles.preview')} />}
      <form className="card form grid-2" onSubmit={submit}>
        <h2>{editing ? t('common.edit') : t('voiceProfiles.create')}</h2>
        <label>
          {t('voiceProfiles.name')}
          <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </label>
        <label>
          {t('voiceProfiles.language')}
          <input required value={form.language} onChange={(e) => setForm({ ...form, language: e.target.value })} />
        </label>
        <label>
          {t('voiceProfiles.provider')}
          <select value={form.provider} onChange={(e) => setForm({ ...form, provider: e.target.value })}>
            {PROVIDERS.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
        </label>
        <label>
          {t('voiceProfiles.voice')}
          <input
            required
            list="voice-options"
            value={form.voice_id}
            onChange={(e) => setForm({ ...form, voice_id: e.target.value })}
          />
          <datalist id="voice-options">
            {languageVoices.map((v) => (
              <option key={v.id} value={v.id}>
                {v.name}
              </option>
            ))}
          </datalist>
        </label>
        <label>
          {t('voiceProfiles.emotion')}
          <select value={form.emotion ?? 'neutral'} onChange={(e) => setForm({ ...form, emotion: e.target.value })}>
            {EMOTIONS.map((em) => (
              <option key={em} value={em}>
                {t(`voiceProfiles.emotions.${em}`)}
              </option>
            ))}
          </select>
        </label>
        <label>
          {t('voiceProfiles.speed')} ({form.speed})
          <input
            type="range"
            min={0.5}
            max={2}
            step={0.1}
            value={form.speed ?? 1}
            onChange={(e) => setForm({ ...form, speed: Number(e.target.value) })}
          />
        </label>
        <label>
          {t('voiceProfiles.pitch')} ({form.pitch})
          <input
            type="range"
            min={-12}
            max={12}
            step={1}
            value={form.pitch ?? 0}
            onChange={(e) => setForm({ ...form, pitch: Number(e.target.value) })}
          />
        </label>
        <div className="actions">
          <button type="submit">{editing ? t('common.save') : t('common.create')}</button>
          <button type="button" className="secondary" onClick={() => void preview(null)} disabled={!form.voice_id}>
            {t('voiceProfiles.preview')}
          </button>
          {editing && (
            <button
              type="button"
              className="secondary"
              onClick={() => {
                setEditing(null);
                setForm(EMPTY);
              }}
            >
              {t('common.cancel')}
            </button>
          )}
        </div>
      </form>
      {profiles.loading && <Loading />}
      {!profiles.loading && !profiles.data?.length && <p className="muted">{t('voiceProfiles.empty')}</p>}
      <ul className="card-list">
        {(profiles.data ?? []).map((p) => (
          <li key={p.id} className="card">
            <h3>
              {p.name} {p.is_default && <span className="badge">{t('voiceProfiles.default')}</span>}
            </h3>
            <p className="muted">
              {p.language} · {p.provider} · {p.voice_id} · {t(`voiceProfiles.emotions.${p.emotion ?? 'neutral'}`)}
            </p>
            <div className="actions">
              <button type="button" onClick={() => void preview(p)}>
                {t('voiceProfiles.preview')}
              </button>
              {!p.is_default && (
                <button type="button" className="secondary" onClick={() => void act(() => endpoints.voiceProfiles.setDefault(p.id))}>
                  {t('voiceProfiles.makeDefault')}
                </button>
              )}
              <button
                type="button"
                className="secondary"
                onClick={() => {
                  setEditing(p.id);
                  setForm({
                    name: p.name,
                    language: p.language,
                    provider: p.provider,
                    voice_id: p.voice_id,
                    emotion: p.emotion ?? 'neutral',
                    speed: p.speed ?? 1,
                    pitch: p.pitch ?? 0,
                    settings: p.settings ?? {},
                  });
                }}
              >
                {t('common.edit')}
              </button>
              <button
                type="button"
                className="danger"
                aria-label={`${t('common.delete')}: ${p.name}`}
                onClick={() => setDeleting(p)}
              >
                {t('common.delete')}
              </button>
            </div>
          </li>
        ))}
      </ul>
      {deleting && (
        <ConfirmDialog
          title={t('common.delete')}
          message={`${deleting.name}: ${t('common.confirmDelete')}`}
          onClose={() => setDeleting(null)}
          onConfirm={() => void act(() => endpoints.voiceProfiles.remove(deleting.id))}
        />
      )}
    </div>
  );
}
